import * as React from 'react'

import { reaction } from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import { ISitePermitFacets } from '~/client/graph'
import NavBar from '~/client/src/desktop/components/NavBar/NavBar'
import CalendarPlaceholderStore from '~/client/src/shared/components/CalendarDayView/components/CalendarPlaceholder.store'
import * as Layout from '~/client/src/shared/components/Layout'
import { Loader } from '~/client/src/shared/components/Loader'
import MapExportStore from '~/client/src/shared/components/MapExport/MapExport.store'
import { UrlParamKey } from '~/client/src/shared/constants/commonRoutes'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import Workflow from '~/client/src/shared/models/Workflow'
import BasemapsStore from '~/client/src/shared/stores/domain/Basemaps.store'
import ClosuresStore from '~/client/src/shared/stores/domain/Closures.store'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import GlobeViewsStore from '~/client/src/shared/stores/domain/GlobeViews.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import ProjectsStore from '~/client/src/shared/stores/domain/Projects.store'
import SitemapsStore from '~/client/src/shared/stores/domain/Sitemaps.store'
import SitePermitFollowingsStore from '~/client/src/shared/stores/domain/SitePermitFollowings.store'
import TilesetsStore from '~/client/src/shared/stores/domain/Tilesets.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import WorkflowCategoriesStore from '~/client/src/shared/stores/domain/WorkflowCategories.store'
import WorkflowsStore from '~/client/src/shared/stores/domain/Workflows.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import WorkflowDrawingGuardStore from '~/client/src/shared/stores/ui/WorkflowDrawingGuard.store'
import { resolveFormTypeDeepLink } from '~/client/src/shared/utils/resolveFormTypeDeepLink'
import { toast } from '~/client/src/shared/utils/toast'
import { removeFromSearchParam } from '~/client/src/shared/utils/urlutils'

import SharedSitePermitStore from '../../../../../shared/stores/domain/SharedSitePermit.store'
import ViewModes from '../../../../enums/ViewModes'
import DesktopInitialState from '../../../../stores/DesktopInitialState'
import DesktopEventStore from '../../../../stores/EventStore/DesktopEvents.store'
import ProjectSetUp from '../../../ProjectSetUp/ProjectSetUp'
import LogisticsActionSideWrapper from '../LogisticsActionSideWrapper'
import LogisticsCalendar from '../LogisticsCalendar/LogisticsCalendar'
import DesktopLogisticsCalendarStore from '../LogisticsCalendar/LogisticsCalendar.store'
import LogisticsDialogs from '../LogisticsDialogs'
import LogisticsGantt from '../LogisticsGantt/LogisticsGantt'
import LogisticsGanttStore from '../LogisticsGantt/LogisticsGantt.store'
import LogisticsHeaderBar from '../LogisticsHeaderBar/LogisticsHeaderBar'
import LogisticsList from '../LogisticsList/LogisticsList'
import LogisticsListStore from '../LogisticsList/LogisticsList.store'
import LogisticsListFacetStore from '../LogisticsList/LogisticsListFacet.store'
import LogisticsMap from '../LogisticsMap/LogisticsMap'
import LogisticsMapStore from '../LogisticsMap/LogisticsMap.store'
import LogisticsStore from './Logistics.store'

import './Logistics.scss'

export interface IProps {
  eventsStore?: DesktopEventStore
  sitemapsStore?: SitemapsStore
  projectsStore?: ProjectsStore
  projectDateStore?: ProjectDateStore
  workflowsStore?: WorkflowsStore
  locationAttributesStore?: LocationAttributesStore
  closuresStore?: ClosuresStore
  workflowTypesStore?: WorkflowTypesStore
  companiesStore?: CompaniesStore
  projectMembersStore?: ProjectMembersStore
  workflowFollowingsStore?: SitePermitFollowingsStore
  userProjectsStore?: UserProjectsStore
  calendarPlaceholderStore?: CalendarPlaceholderStore
  globeViewsStore?: GlobeViewsStore
  basemapsStore?: BasemapsStore
  sharedSitePermitStore?: SharedSitePermitStore
  workflowCategoriesStore?: WorkflowCategoriesStore
  tilesetsStore?: TilesetsStore
  state?: DesktopInitialState
  workflowDrawingGuardStore?: WorkflowDrawingGuardStore

  onDatesFilterChange?: (datesFilter: {
    startDate?: number
    endDate?: number
  }) => void
  workflows?: Workflow[]
  viewableFacets?: ISitePermitFacets
  totalFacets?: ISitePermitFacets
  deepLinkedForm?: Workflow
  deepLinkFormType?: string
}

@inject(
  'eventsStore',
  'sitemapsStore',
  'projectsStore',
  'projectDateStore',
  'workflowsStore',
  'locationAttributesStore',
  'closuresStore',
  'workflowTypesStore',
  'companiesStore',
  'projectMembersStore',
  'workflowFollowingsStore',
  'userProjectsStore',
  'calendarPlaceholderStore',
  'globeViewsStore',
  'basemapsStore',
  'sharedSitePermitStore',
  'workflowCategoriesStore',
  'tilesetsStore',
  'state',
  'workflowDrawingGuardStore',
)
@observer
export default class Logistics extends React.Component<IProps> {
  private readonly store: LogisticsStore
  private readonly logisticsListStore: LogisticsListStore = null
  private readonly logisticsMapStore: LogisticsMapStore = null
  private readonly logisticsCalendarStore: DesktopLogisticsCalendarStore = null
  private readonly logisticsGanttStore: LogisticsGanttStore = null
  private readonly logisticsListFacetStore: LogisticsListFacetStore = null
  private readonly mapExportStore = new MapExportStore()

  private dispose: () => void
  private disposeFormTypeReaction: () => void

  public constructor(props: IProps) {
    super(props)

    this.store = new LogisticsStore(
      props.workflowsStore,
      props.projectDateStore,
      props.eventsStore,
      props.sitemapsStore,
      props.globeViewsStore,
      props.tilesetsStore,
      props.closuresStore,
      props.sharedSitePermitStore,
      props.workflowTypesStore,
      props.workflowCategoriesStore,
      props.locationAttributesStore,
      props.basemapsStore,
      props.calendarPlaceholderStore,
      props.companiesStore,
      props.workflowDrawingGuardStore,
    )

    this.logisticsListStore = new LogisticsListStore(
      this.store,
      props.projectDateStore,
      props.workflowsStore,
      props.locationAttributesStore,
      props.companiesStore,
      props.projectMembersStore,
      props.userProjectsStore,
      props.projectsStore,
      props.state,
      props.eventsStore,
      props.workflowFollowingsStore,
      props.workflowTypesStore,
      props.workflowCategoriesStore,
      props.sharedSitePermitStore,
    )

    this.logisticsListFacetStore = new LogisticsListFacetStore(
      () => this.logisticsListStore.filteredCollection,
      () => this.store.allLogistics,
      this.logisticsListStore.getFilteredCollectionExcludeFilter.bind(
        this.logisticsListStore,
      ),
      filterType =>
        this.logisticsListStore.activeFilterTypes.includes(filterType),
      props.eventsStore.appState.activeProject.id,
    )

    this.logisticsMapStore = new LogisticsMapStore(
      this.store,
      props.companiesStore,
      props.state,
    )

    this.logisticsCalendarStore = new DesktopLogisticsCalendarStore(
      this.store,
      this.logisticsListStore,
      props.calendarPlaceholderStore,
      props.projectDateStore,
      props.companiesStore,
      props.workflowTypesStore,
      props.locationAttributesStore,
      props.sharedSitePermitStore,
    )

    this.logisticsGanttStore = new LogisticsGanttStore(
      this.store,
      props.projectDateStore,
      props.workflowsStore,
      props.locationAttributesStore,
      props.companiesStore,
      props.projectMembersStore,
      props.userProjectsStore,
      props.projectsStore,
      props.state,
      props.eventsStore,
      props.workflowFollowingsStore,
      props.workflowTypesStore,
      props.workflowCategoriesStore,
      props.sharedSitePermitStore,
    )

    this.store.initPostEventCallback()
  }

  public componentDidUpdate(prevProps: IProps): void {
    this.store.ensureCorrectViewMode()

    if (prevProps.workflows !== this.props.workflows) {
      this.store.setAllForms(this.props.workflows)
      this.logisticsMapStore.setAllForms(this.props.workflows)
    }
    if (prevProps.viewableFacets !== this.props.viewableFacets) {
      this.logisticsListFacetStore.setViewableWorkflowFacets(
        this.props.viewableFacets,
      )
    }
    if (prevProps.totalFacets !== this.props.totalFacets) {
      this.logisticsListFacetStore.setTotalWorkflowFacets(
        this.props.totalFacets,
      )
    }
    if (!prevProps.deepLinkedForm && this.props.deepLinkedForm) {
      this.store.showWorkflowViewDialog(this.props.deepLinkedForm)
      this.store.dateFilters.startDate = this.props.projectDateStore.startOfDay(
        this.props.deepLinkedForm.startDate,
      )
    }

    this.tryOpenFormTypeFromDeepLink()
  }

  public componentDidMount(): void {
    const {
      eventsStore: { appState },
    } = this.props
    this.store.setInitialDateMode()
    const { openPermitCreationForm, hideAllDialogs } = this.store

    this.dispose = reaction(
      () => [this.store.startDate, this.store.endDate],
      () => {
        this.props.onDatesFilterChange({
          startDate: this.store.startDate.getTime(),
          endDate: this.store.endDate.getTime(),
        })
      },
      { fireImmediately: true },
    )

    if (this.tryOpenFormTypeFromDeepLink()) {
      return
    }

    // When permit types haven't loaded yet, retry the deep link once they arrive.
    if (
      this.props.deepLinkFormType &&
      !this.props.workflowTypesStore.isDataReceived
    ) {
      this.disposeFormTypeReaction = reaction(
        () => this.props.workflowTypesStore.isDataReceived,
        isReady => {
          if (isReady) {
            this.tryOpenFormTypeFromDeepLink()
            this.disposeFormTypeReaction?.()
          }
        },
      )
    }

    if (appState.preSelectedPermitTypeId && !this.handledFormType) {
      return openPermitCreationForm()
    }

    return hideAllDialogs()
  }

  private handledFormType: string = null

  private tryOpenFormTypeFromDeepLink(): boolean {
    const { deepLinkFormType, workflowTypesStore, eventsStore } = this.props

    const result = resolveFormTypeDeepLink({
      deepLinkFormType,
      handledFormType: this.handledFormType,
      isDataReceived: workflowTypesStore.isDataReceived,
      enabledTypes: workflowTypesStore.enabledTypes,
      allTypes: workflowTypesStore.actualTypes,
    })

    switch (result.action) {
      case 'skip':
        if (!deepLinkFormType) this.handledFormType = null
        return false
      case 'wait':
        return false
      case 'open':
        this.handledFormType = result.handledFormType
        removeFromSearchParam(UrlParamKey.FormType)
        eventsStore.appState.preSelectedPermitTypeId = result.permitTypeId
        this.store.openPermitCreationForm()
        return true
      case 'deny':
        this.handledFormType = result.handledFormType
        removeFromSearchParam(UrlParamKey.FormType)
        toast.error(Localization.translator.noPermissionToCreateWorkflowType)
        return false
      case 'not_found':
        this.handledFormType = result.handledFormType
        removeFromSearchParam(UrlParamKey.FormType)
        return false
    }
  }

  public componentWillUnmount(): void {
    this.mapExportStore.dispose()
    this.store.clearPostEventCallback()
    this.store.hideAllDialogs()
    this.disposeFormTypeReaction?.()

    if (this.dispose) {
      this.dispose()
    }
  }

  public render(): JSX.Element {
    const { projectsStore, projectDateStore } = this.props
    const { isLoaderShown, isCalendarViewMode } = this.store

    return (
      <Layout.View className="logistics">
        <Layout.Header className="layout-navigation">
          <NavBar />
        </Layout.Header>
        <Layout.View className="relative overflow-hidden">
          <div className="row full-height">
            <ProjectSetUp
              className="col full-height w-fit-content"
              sidebarOnly
            />
            <div className="col full-height">
              {!isLoaderShown && (
                <LogisticsDialogs
                  logisticsStore={this.store}
                  logisticsCalendarStore={this.logisticsCalendarStore}
                />
              )}
              {this.store.selectedSharedEntity && (
                <LogisticsActionSideWrapper
                  id={this.store.selectedSharedEntity.id}
                  onClose={this.store.resetSelectedSharedEntity}
                />
              )}
              <Layout.Header>
                {!isLoaderShown && (
                  <LogisticsHeaderBar
                    logisticsStore={this.store}
                    logisticsListStore={this.logisticsListStore}
                    logisticsGanttStore={this.logisticsGanttStore}
                    projectsStore={projectsStore}
                    projectDateStore={projectDateStore}
                    isMapViewDisabled={this.store.isMapViewDisabled}
                    logisticsListFacetStore={this.logisticsListFacetStore}
                  />
                )}
              </Layout.Header>
              <Layout.Content>
                <div className="relative-block overflow-hidden multi-grid-container">
                  <div
                    className={classList({
                      'full-height': true,
                      row: isCalendarViewMode,
                    })}
                  >
                    {this.renderView()}
                  </div>
                </div>
              </Layout.Content>
            </div>
          </div>
        </Layout.View>
        <Layout.Footer>
          <div />
        </Layout.Footer>
      </Layout.View>
    )
  }

  private renderView(): JSX.Element {
    if (this.store.isLoaderShown) {
      return <Loader hint={Localization.translator.loadingFormsData} />
    }

    const { viewMode } = this.store

    switch (viewMode) {
      case ViewModes.Gantt:
        return (
          <LogisticsGantt
            logisticsStore={this.store}
            store={this.logisticsGanttStore}
          />
        )
      case ViewModes.Calendar:
        return (
          <LogisticsCalendar
            logisticsStore={this.store}
            store={this.logisticsCalendarStore}
          />
        )
      case ViewModes.List:
        return (
          <LogisticsList
            logisticsStore={this.store}
            store={this.logisticsListStore}
          />
        )
      case ViewModes.Map:
        return (
          <LogisticsMap
            store={this.store}
            logisticsMapStore={this.logisticsMapStore}
            logisticsListStore={this.logisticsListStore}
            mapExportStore={this.mapExportStore}
          />
        )
    }
  }
}
